import { useEffect, useRef, useState } from 'react';
import { Check } from 'shared/icons';
import { NeedsCaseReconcile } from '../../constants/pedidoTags';
import { ChecklistLines, INSPECTION_CHECK_STEPS, INSPECTION_PHOTO_UNSTAGED } from '../../constants/rmaInspection';
import { ResolveAssetUrl } from '../../utils/assetUrl';
import { FormatDateTimeBr } from '../../utils/date';
import { CountLabel } from '../../utils/format';
import { BuildRmaLabelsHtml } from '../../utils/rmaLabels';
import Button from '../ui/Button';
import Input from '../ui/Input';
import LoadingSpinner from '../ui/LoadingSpinner';
import Modal from '../ui/Modal';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';

// ── Fundações visuais do módulo RMA ──────────────────────────────────────────
// Filosofia (redesign 2026-08-08): hierarquia CALMA. Cada RMA tem UMA cor de
// estado (faixa + pill); tags extras só para o excepcional (mediação a
// responder, SLA, avaria). Nada de arco-íris por linha.

export const PIPELINE = ['ABERTO', 'EM_TRANSITO', 'RECEBIDO', 'INSPECIONADO', 'RESOLVIDO'];

export const STATUS_META = {
    ABERTO: {
        label: 'Aberto',
        strip: 'bg-sky-400 dark:bg-sky-500',
        pill: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-400/30',
    },
    EM_TRANSITO: {
        label: 'Em trânsito',
        strip: 'bg-violet-400 dark:bg-violet-500',
        pill: 'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-400/10 dark:text-violet-300 dark:ring-violet-400/30',
    },
    RECEBIDO: {
        label: 'Recebido',
        strip: 'bg-amber-400 dark:bg-amber-500',
        pill: 'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/30',
    },
    INSPECIONADO: {
        label: 'Inspecionado',
        strip: 'bg-orange-400 dark:bg-orange-500',
        pill: 'bg-orange-50 text-orange-800 ring-orange-600/20 dark:bg-orange-400/10 dark:text-orange-300 dark:ring-orange-400/30',
    },
    RESOLVIDO: {
        label: 'Resolvido',
        strip: 'bg-emerald-400 dark:bg-emerald-500',
        pill: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30',
    },
    CANCELADO: {
        label: 'Encerrado',
        strip: 'bg-mono-300 dark:bg-mono-700',
        pill: 'bg-mono-100 text-mono-500 ring-mono-400/20 dark:bg-mono-900 dark:text-mono-400 dark:ring-mono-600/30',
    },
};

export const RESOLUTION_LABELS = {
    REESTOQUE: 'Reestoque',
    DESCARTE: 'Descarte',
    REPARO: 'Reparo',
    DEVOLUCAO_FORNECEDOR: 'Devolução ao fornecedor',
    GARANTIA_FABRICANTE: 'Garantia (fabricante)',
};

export const REFUND_LABELS = {
    PENDENTE: 'Pendente',
    REEMBOLSADO: 'Reembolsado',
    PARCIAL: 'Parcial',
    NEGADO: 'Negado',
    NAO_APLICAVEL: 'Não se aplica',
};

export const RESPONSIBLE_LABELS = {
    // A doc oficial documenta seller|buyer|mediator; a API na prática devolve
    // respondent/complainant — mapeamos os dois formatos.
    respondent: 'Nossa equipe',
    seller: 'Nossa equipe',
    complainant: 'Comprador',
    buyer: 'Comprador',
    mediator: 'Mediador ML',
};

export const SUPPLIER_OUTCOME_LABELS = {
    CREDITO: 'Crédito do fornecedor',
    REEMBOLSO: 'Reembolso em dinheiro',
    REPOSICAO: 'Reposição / troca',
    CONSERTO: 'Consertada / reparada',
    DESCARTE: 'Descarte',
    SEM_RETORNO: 'Sem retorno',
};

export const MOVEMENT_STATUS_LABELS = {
    PENDENTE: 'aguardando envio ao Bling',
    ENVIADO: 'lançado no Bling',
    ERRO: 'erro no envio — retry automático',
    CANCELADO: 'cancelado',
};

export const fmtDate = (iso) => FormatDateTimeBr(iso, { blank: '—' });

// Identidade do RMA = número da reclamação do ML; manuais usam o id interno.
/**
 * Página da venda no painel do vendedor do ML — é onde a conversa da
 * reclamação/mediação aparece com a caixa de resposta (mesmo link que as
 * notificações do ML usam). Sem pedido, cai na lista de reclamações.
 */
export function MlClaimUrl(rma, packId = null) {
    // Pedido dentro de um pack: o painel do ML endereça pelo PACK.
    const pack = [rma?.pack_id, packId].find((value) => value && String(value) !== 'null') || null;
    const reference = pack || rma?.external_order_id;
    if (reference) {
        return `https://www.mercadolivre.com.br/vendas/${reference}/detalhe`;
    }
    return 'https://www.mercadolivre.com.br/vendas/reclamacoes';
}

export function rmaIdentifier(rma) {
    if (rma.external_claim_id) {
        return `#${rma.external_claim_id}`;
    }
    // RMA manual: os PRIMEIROS hex do UUIDv7 são timestamp (colidem para
    // registros criados na mesma janela) — a parte aleatória fica no fim.
    return `Ocorrência ${String(rma.id || '')
        .replace(/-/g, '')
        .slice(-8)
        .toUpperCase()}`;
}

/**
 * O ML expõe DOIS prazos: o do caso (detail.due_date — o que o painel mostra)
 * e o da ação obrigatória (às vezes uma janela administrativa longa). O que
 * importa para o operador é o MAIS PRÓXIMO.
 */
export function NearestDue(rma) {
    const dates = [rma?.ml_mandatory_due, rma?.mediation_due_date]
        .filter(Boolean)
        .map((d) => new Date(d))
        .filter((d) => !Number.isNaN(d.getTime()));
    if (!dates.length) {
        return null;
    }
    return new Date(Math.min(...dates.map((d) => d.getTime())));
}

export function dueInfo(dueDate) {
    if (!dueDate) {
        return null;
    }
    const diffMs = new Date(dueDate).getTime() - Date.now();
    const hours = Math.abs(diffMs) / 3600_000;
    const human =
        hours < 1
            ? `${Math.max(1, Math.round(hours * 60))}min`
            : hours < 24
              ? `${Math.round(hours)}h`
              : `${Math.round(hours / 24)}d`;
    if (diffMs < 0) {
        return { label: `estourado há ${human}`, tone: 'text-danger-600 dark:text-danger-400', breached: true };
    }
    if (hours <= 24) {
        return { label: `vence em ${human}`, tone: 'text-warning-text dark:text-warning-300', breached: false };
    }
    return { label: `vence em ${human}`, tone: 'text-mono-500 dark:text-mono-400', breached: false };
}

/**
 * Caso ENCERRADO: o ML fechou o claim (status ≠ opened) ou nós encerramos o
 * caso (RESOLVIDO/CANCELADO). Prazo vencido de caso encerrado não é
 * urgência — vira "encerrado", neutro (`CaseDueInfo`).
 */
export function IsCaseClosed(rma) {
    return (
        Boolean(rma?.ml_claim_status && rma.ml_claim_status !== 'opened') ||
        ['RESOLVIDO', 'CANCELADO'].includes(rma?.status)
    );
}

/** `dueInfo` do prazo mais próximo; caso encerrado com prazo vencido → "encerrado", neutro. */
export function CaseDueInfo(rma) {
    const due = dueInfo(NearestDue(rma));
    if (due?.breached && IsCaseClosed(rma)) {
        return { label: 'encerrado', tone: 'text-mono-500 dark:text-mono-400', breached: false, closed: true };
    }
    return due;
}

function timeOrNull(value) {
    if (!value) {
        return null;
    }
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
}

/**
 * Recontato (caso REABERTO depois do encerramento) com a vez NOSSA — espelho
 * da fila `recontatos_responder` do backend (RmaQueuePredicates), decisão de
 * 2026-09-23: o ML não preenche ações no recontato; a vez é nossa quando o
 * responsável é o vendedor (`respondent`), o claim está aberto e o prazo da
 * mediação ainda não venceu — ou, raro, com ação OBRIGATÓRIA nossa na janela
 * viva. Os demais recontatos estão em análise pelo ML. A resposta é dada no
 * painel do ML (a API não oferece ação).
 */
export function RecontactOurTurn(rma, now = Date.now()) {
    if (rma?.mediation_status !== 'RECONTATO') {
        return false;
    }
    const mandatoryDue = timeOrNull(rma.ml_mandatory_due);
    const liveWindow =
        !rma.ml_claim_status || rma.ml_claim_status === 'opened' || (mandatoryDue !== null && mandatoryDue >= now);
    if (rma.ml_mandatory_action && liveWindow) {
        return true;
    }
    const due = timeOrNull(rma.mediation_due_date);
    return rma.ml_claim_status === 'opened' && rma.mediation_responsible === 'respondent' && due !== null && due >= now;
}

export function timeAgo(iso) {
    if (!iso) {
        return '—';
    }
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 60) {
        return `há ${Math.max(mins, 1)}min`;
    }
    if (mins < 1440) {
        return `há ${Math.floor(mins / 60)}h`;
    }
    return `há ${Math.floor(mins / 1440)}d`;
}

// ── Pills e tags ─────────────────────────────────────────────────────────────

/** Pill do estado do pipeline — a ÚNICA cor estrutural da linha. */
export const StatusPill = ({ status }) => {
    // Valor desconhecido aparece CRU em pill neutra — sinal de estado novo a
    // catalogar, nunca disfarçado de "Encerrado" (estudo R9).
    const meta = STATUS_META[status] || {
        label: status || '—',
        pill: 'bg-mono-100 text-mono-500 ring-mono-400/20 dark:bg-mono-900 dark:text-mono-400 dark:ring-mono-600/30',
    };
    return (
        <span
            className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${meta.pill}`}
        >
            {meta.label}
        </span>
    );
};

/** Tag discreta para o excepcional (só aparece quando exige atenção). */
export const AlertTag = ({ tone = 'neutral', children, title }) => {
    const tones = {
        urgent: 'bg-danger-600 text-white dark:bg-danger-500 dark:text-white',
        danger: 'bg-danger-50 text-danger-700 ring-1 ring-inset ring-danger-600/20 dark:bg-danger-400/10 dark:text-danger-300',
        warning:
            'bg-warning-50 text-warning-800 ring-1 ring-inset ring-warning-600/20 dark:bg-warning-400/10 dark:text-warning-300',
        finance: 'bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-600/20 dark:bg-cyan-400/10 dark:text-cyan-300',
        neutral: 'bg-mono-100 text-mono-500 dark:bg-mono-900 dark:text-mono-400',
    };
    return (
        <span
            title={title}
            className={`inline-flex items-center gap-1 whitespace-nowrap px-2.5 py-0.5 text-[11px] font-semibold ${tones[tone]}`}
        >
            {children}
        </span>
    );
};

/**
 * Tags de exceção de um RMA — apenas o que muda a leitura da linha.
 * O estado do pipeline NÃO entra aqui (ele tem a faixa/pill próprios).
 */
export const RmaAlertTags = ({ rma }) => {
    const due = dueInfo(rma.mediation_due_date);
    const slaBreached = (rma.slas || []).some((s) => s.breached && !s.done);
    const needsReconcile = ['REEMBOLSADO', 'PARCIAL'].includes(rma.refund_status) && NeedsCaseReconcile(rma);
    // "Bola conosco" oficial do ML: a ação OBRIGATÓRIA do claim é nossa.
    const pendingOurs = Boolean(rma.ml_mandatory_action);
    const tags = [];
    if (rma.mediation_status === 'EM_MEDIACAO') {
        if (pendingOurs) {
            tags.push(
                <AlertTag key="med" tone="urgent" title={due?.label}>
                    Responder mediação{due ? ` · ${due.label}` : ''}
                </AlertTag>,
            );
        } else {
            tags.push(
                <AlertTag key="med" tone={due?.breached ? 'danger' : 'warning'} title={due?.label}>
                    Mediação{due ? ` · ${due.label}` : ''}
                </AlertTag>,
            );
        }
    } else if (rma.mediation_status === 'RECONTATO') {
        // Caso REABERTO pós-encerramento: "responder" só com a vez NOSSA
        // (`RecontactOurTurn` — regra da fila recontatos_responder); senão o
        // ML está analisando e não há nada a responder.
        tags.push(
            RecontactOurTurn(rma) ? (
                <AlertTag key="recontato" tone="urgent" title={due?.label}>
                    Recontato — responder{due ? ` · ${due.label}` : ''}
                </AlertTag>
            ) : (
                <AlertTag key="recontato" tone="neutral">
                    Recontato em análise pelo ML
                </AlertTag>
            ),
        );
    } else if (rma.claim_stage === 'claim' && pendingOurs) {
        // Fase inicial do claim (reclamação, sem mediação) esperando a gente.
        tags.push(
            <AlertTag key="rec" tone="urgent" title={due?.label}>
                Responder reclamação{due ? ` · ${due.label}` : ''}
            </AlertTag>,
        );
    }
    if (slaBreached) {
        tags.push(
            <AlertTag key="sla" tone="danger">
                SLA estourado
            </AlertTag>,
        );
    }
    if (rma.inspection_damage === true && rma.status !== 'RESOLVIDO') {
        tags.push(
            <AlertTag key="avaria" tone="warning">
                Com avaria
            </AlertTag>,
        );
    }
    if (needsReconcile) {
        tags.push(
            <AlertTag key="conc" tone="finance">
                Conciliar reembolso
            </AlertTag>,
        );
    }
    if (rma.is_full) {
        tags.push(
            <AlertTag key="full" tone="neutral">
                Full
            </AlertTag>,
        );
    }
    if (!tags.length) {
        return null;
    }
    return <div className="flex flex-wrap items-center justify-end gap-1">{tags}</div>;
};

// ── Trilhos do RMA (2026-08-11) ──────────────────────────────────────────────
// O pós-venda são QUATRO processos com donos e ritmos próprios:
//   ml         — reclamação/mediação, vive no Mercado Livre
//   fisico     — a mercadoria no galpão (só quando ela vem para nós)
//   fabricante — RMA com o fornecedor (nasce no desfecho fornecedor/garantia)
//   financeiro — reembolso, conciliação e NFs
// Cada um tem estado próprio; o RMA como um todo só conclui com todos fechados.
// Estados: na (não se aplica) | done | pendente (com tone: urgent|warn|info|neutral)

/**
 * A devolução está parada numa agência (Correios/ML) esperando a NOSSA
 * retirada? Espelho do predicado `devolucoes_retirar` (tag
 * `devolucao_para_retirar`): substatus `waiting_for_withdrawal` do envio de
 * devolução, envio não entregue e sem data real de entrega, caso ainda não
 * recebido (sem `received_at`, fora de RECEBIDO/INSPECIONADO) e sem
 * etiqueta. NÃO depende do status do caso — CANCELADO/reembolsado parado na
 * agência também é retirada (4 casos em 23/09). O destino (vem para nós) é de
 * quem chama: no trilho físico, Full/armazém do ML já saíram como `na`.
 */
export function IsAwaitingWithdrawal(rma) {
    return Boolean(
        rma &&
            rma.return_shipment_substatus === 'waiting_for_withdrawal' &&
            rma.return_shipment_status !== 'delivered' &&
            !rma.return_delivered_at &&
            !rma.received_at &&
            !['RECEBIDO', 'INSPECIONADO'].includes(rma.status) &&
            rma.has_label !== true,
    );
}

export function ComputeTracks(rma) {
    const mandatoryOurs = Boolean(rma.ml_mandatory_action);
    // Veredito da devolução: o ML costuma pedir SEM mandatory, mas com prazo e
    // consequência (silêncio = "recebeu conforme o esperado").
    const reviewOurs = (rma.ml_pending_actions || []).some((a) => String(a).startsWith('return_review'));

    // — Mercado Livre — "responder" só quando a ação OBRIGATÓRIA é nossa (é o
    // que o painel do vendedor considera); ação facultativa não é pendência.
    // O veredito da devolução vence qualquer desfecho: a janela de revisão
    // vive mesmo com o caso já fechado no ML (é o que o painel mostra).
    let ml;
    if (!rma.external_claim_id) {
        ml = { na: true, label: 'sem caso ML' };
    } else if (reviewOurs) {
        ml = { label: 'revisar devolução', tone: 'urgent' };
    } else if (rma.mediation_status === 'RECONTATO') {
        // Caso REABERTO depois do encerramento. A vez é NOSSA pela regra da
        // fila recontatos_responder (`RecontactOurTurn`: respondent + claim
        // aberto + prazo por vencer); senão o ML está analisando
        // (recontato_com_ml) — nada a responder.
        if (RecontactOurTurn(rma)) {
            ml = { label: 'responder recontato', tone: 'urgent' };
        } else if (rma.ml_claim_status === 'closed') {
            ml = { done: true, label: 'encerrada' };
        } else {
            ml = { label: 'recontato em análise pelo ML', tone: 'info' };
        }
    } else if (rma.mediation_status === 'CASO_ML') {
        // Comprador × Mercado Livre (ml_case/stale) — não somos parte da briga.
        ml =
            rma.ml_claim_status === 'closed'
                ? { done: true, label: 'encerrada' }
                : { label: 'caso com o ML', tone: 'info' };
    } else if (rma.mediation_status === 'GANHA') {
        ml = { done: true, label: 'ganha', tone: 'ok' };
    } else if (rma.mediation_status === 'PERDIDA') {
        ml = { done: true, label: 'perdida', tone: 'bad' };
    } else if (rma.mediation_status === 'COBERTA') {
        // Comprador reembolsado, MAS o ML cobriu a nossa parte.
        ml = { done: true, label: 'coberta pelo ML', tone: 'ok' };
    } else if (rma.mediation_status === 'ENCERRADA') {
        ml = { done: true, label: 'encerrada' };
    } else if (
        rma.ml_claim_status === 'closed' ||
        (rma.claim_stage && rma.mediation_status === 'SEM_MEDIACAO' && rma.ml_resolution_reason)
    ) {
        // Caso formalmente fechado no ML — com ou sem reason (linhas antigas
        // do sync não têm ml_claim_status; o reason segura a compatibilidade).
        ml = { done: true, label: 'encerrada' };
    } else if (rma.mediation_status === 'EM_MEDIACAO') {
        ml = mandatoryOurs
            ? { label: 'responder mediação', tone: 'urgent' }
            : { label: 'mediação em curso', tone: 'info' };
    } else {
        ml = mandatoryOurs
            ? { label: 'responder reclamação', tone: 'urgent' }
            : { label: 'reclamação em curso', tone: 'info' };
    }

    // — Físico (galpão) —
    let fisico;
    const wentToWarehouse = rma.ml_resolution_reason === 'warehouse_decision';
    // `is_full` marca a VENDA Full; a devolução dela pode vir ao NOSSO
    // endereço (return_destination 'seller_address' — ex. do dono, pedido
    // 2000018152542350: PAC parado na agência). Rodada 7: mesma leitura do
    // `returnToUs` do backend — só é "armazém ML" quando o destino não é nosso.
    if (!HasPhysicalFlow(rma)) {
        fisico = { na: true, label: 'Full — armazém ML' };
    } else if (rma.source === 'ML' && !rma.return_id) {
        fisico = { na: true, label: 'sem devolução física' };
    } else if (rma.return_destination === 'warehouse' && !rma.received_at) {
        // Doc oficial: destination warehouse = a devolução vai para o depósito
        // do ML — não passa pelo nosso galpão.
        fisico = { na: true, label: 'vai ao armazém ML' };
    } else if (wentToWarehouse && rma.return_destination !== 'seller_address' && !rma.received_at) {
        // warehouse_decision NÃO garante que a mercadoria fica lá: com triagem
        // (return_from_triage) o destino final pode ser o nosso galpão — nesse
        // caso o trilho físico segue vivo (estudo R6).
        fisico = { na: true, label: 'decidido no armazém ML' };
    } else if (IsAwaitingWithdrawal(rma)) {
        // Parada numa agência (Correios/ML) esperando a NOSSA retirada —
        // ação da Logística. Antes de "encerrado" e de "devolução falhou": o
        // nível da devolução marca not_delivered depois da 1ª tentativa e o
        // caso pode estar CANCELADO/reembolsado, mas a mercadoria está lá.
        fisico = { label: 'retirar na agência', tone: 'warn' };
    } else if (rma.status === 'RESOLVIDO') {
        fisico = { done: true, label: rma.resolution ? RESOLUTION_LABELS[rma.resolution] : 'destinada', tone: 'ok' };
    } else if (rma.status === 'CANCELADO') {
        fisico = { done: true, label: 'encerrado' };
    } else if (rma.status === 'INSPECIONADO') {
        fisico = { label: 'destinar', tone: 'warn' };
    } else if (rma.has_label === true) {
        // Processo de devolução (Thiago, 2026-09-23): a LOGÍSTICA recebe e
        // cola a etiqueta; o ATENDIMENTO inspeciona toda devolução etiquetada
        // (etiqueta ASSOCIADA ⇒ a mercadoria está no galpão). `has_label` só
        // vale quando vem como boolean — sem ele, as etapas de antes.
        fisico = { label: 'inspecionar', tone: 'urgent' };
    } else if (rma.has_label === false && (rma.status === 'RECEBIDO' || rma.return_shipment_status === 'delivered')) {
        // Chegou ao galpão e ainda está sem etiqueta: vez da Logística.
        fisico = { label: 'etiquetar', tone: 'warn' };
    } else if (rma.status === 'RECEBIDO') {
        fisico = { label: 'inspecionar', tone: 'urgent' };
    } else if (rma.return_shipment_status === 'delivered') {
        // O ML confirmou a entrega ('A caminho' aqui era o bug 2026-08-27).
        // Chegada ao CD (rodada 7): a inspeção só vem DEPOIS da etiqueta da
        // Logística (etiquetada = recebida) — entregue sem recebimento é
        // "etiquetar", não "aguardando inspeção".
        fisico = { label: 'etiquetar', tone: 'warn' };
    } else if (['label_generated', 'pending', 'scheduled', 'ready_to_ship'].includes(rma.return_shipment_status)) {
        // Etiqueta gerada ≠ postada: o comprador ainda nem despachou.
        fisico = { label: 'aguardando postagem', tone: 'neutral' };
    } else if (['cancelled', 'expired', 'not_delivered', 'failed'].includes(rma.return_shipment_status)) {
        fisico = { label: 'devolução falhou', tone: 'bad' };
    } else {
        fisico = { label: 'a caminho', tone: 'info' };
    }

    // — Fabricante —
    let fabricante;
    if (!['DEVOLUCAO_FORNECEDOR', 'GARANTIA_FABRICANTE'].includes(rma.resolution)) {
        fabricante = { na: true, label: 'não se aplica' };
    } else if (rma.supplier_concluded_at) {
        fabricante = {
            done: true,
            label: SUPPLIER_OUTCOME_LABELS[rma.supplier_outcome] || 'concluído',
            tone: 'ok',
        };
    } else if (rma.supplier_shipped_at) {
        fabricante = { label: 'aguardando retorno', tone: 'info' };
    } else if (rma.supplier_registered_at) {
        fabricante = { label: 'despachar', tone: 'warn' };
    } else {
        fabricante = { label: 'cadastrar', tone: 'warn' };
    }

    // — Financeiro —
    let financeiro;
    const refunded = ['REEMBOLSADO', 'PARCIAL'].includes(rma.refund_status);
    if (refunded && NeedsCaseReconcile(rma)) {
        financeiro = { label: 'conciliar', tone: 'finance' };
    } else if ((rma.inspection_issue === true || rma.inspection_destination === 'DESCARTE') && !rma.nf_loss_at) {
        // NF de perda (rodada 7 — mesma regra de `NeedsLossNf`, da tag
        // nf_perda_para_emitir e de fiscal.nf_perda_pendente): chegou ERRADO
        // (inspeção com problema) OU o supervisor definiu o destino DESCARTE
        // (ajuste 2 do dono), e a NF não foi marcada — com ou sem reembolso
        // (reembolso negado não tira a perda).
        financeiro = { label: 'NF de perda', tone: 'finance' };
    } else if (rma.reconciliation_not_applicable === true) {
        financeiro = { na: true, label: 'sem movimentação', tone: 'ok' };
    } else if (rma.refund_status === 'NEGADO' || rma.refund_status === 'NAO_APLICAVEL') {
        financeiro = { done: true, label: 'sem reembolso', tone: 'ok' };
    } else if (refunded) {
        financeiro = { done: true, label: 'conciliado', tone: 'ok' };
    } else if (ml.done || ml.na) {
        financeiro = { done: true, label: 'sem pendência' };
    } else if (rma.status_money === 'retained') {
        // Doc oficial: retained = o ML segurou o dinheiro da venda enquanto a
        // devolução corre — o caixa já sente antes de qualquer desfecho.
        financeiro = { label: 'dinheiro retido', tone: 'finance' };
    } else {
        financeiro = { label: 'aguardando ML', tone: 'neutral' };
    }

    const tracks = { ml, fisico, fabricante, financeiro };
    const allDone = Object.values(tracks).every((t) => t.done || t.na);
    return { ...tracks, allDone };
}

const TRACK_NAMES = { ml: 'ML', fisico: 'Galpão', fabricante: 'Fabricante', financeiro: 'Financeiro' };

/**
 * A situação da ocorrência em UMA frase — a manchete da linha na lista e do
 * cabeçalho do detalhe (redesign 2026-08-26). Começa na voz do próprio ML
 * quando existe; depois só os trilhos com pendência.
 */
export function SituationLine(rma, tracksArg = null) {
    const tracks = tracksArg || ComputeTracks(rma);
    const head = rma.ml_status_title || (tracks.ml.na ? 'Ocorrência manual' : `ML: ${tracks.ml.label}`);
    const parts = [head];
    if (!tracks.fisico.na && !tracks.fisico.done) {
        parts.push(`galpão: ${tracks.fisico.label}`);
    }
    if (!tracks.fabricante.na && !tracks.fabricante.done) {
        parts.push(`fabricante: ${tracks.fabricante.label}`);
    }
    if (!tracks.financeiro.done && !tracks.financeiro.na) {
        parts.push(`financeiro: ${tracks.financeiro.label}`);
    }
    return parts.join(' · ');
}

const TRACK_CHIP_TONES = {
    urgent: 'bg-danger-600 text-white dark:bg-danger-500',
    warn: 'bg-warning-50 text-warning-800 ring-1 ring-inset ring-warning-600/25 dark:bg-warning-400/10 dark:text-warning-300',
    finance: 'bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-600/25 dark:bg-cyan-400/10 dark:text-cyan-300',
    info: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-600/25 dark:bg-sky-400/10 dark:text-sky-300',
    ok: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/25 dark:bg-emerald-400/10 dark:text-emerald-300',
    bad: 'bg-danger-50 text-danger-700 ring-1 ring-inset ring-danger-600/25 dark:bg-danger-400/10 dark:text-danger-300',
    neutral: 'bg-mono-100 text-mono-500 dark:bg-mono-900 dark:text-mono-400',
};

/**
 * Mini-chips dos trilhos — um por processo APLICÁVEL. Concluído fica quieto
 * (✓); pendente carrega a cor do estado. Trilhos não aplicáveis somem.
 */
export const TrackChips = ({ rma, size = 'sm' }) => {
    const { ml, fisico, fabricante, financeiro } = ComputeTracks(rma);
    const entries = Object.entries({ ml, fisico, fabricante, financeiro }).filter(([, t]) => !t.na);
    if (!entries.length) {
        return null;
    }
    const pad = size === 'md' ? 'px-2.5 py-1 text-[12px]' : 'px-2 py-0.5 text-[11px]';
    return (
        <div className="flex flex-wrap items-center gap-1">
            {entries.map(([key, t]) => (
                <span
                    key={key}
                    className={`inline-flex items-center gap-1 whitespace-nowrap font-semibold ${pad} ${
                        t.done
                            ? TRACK_CHIP_TONES[t.tone === 'bad' ? 'bad' : 'neutral']
                            : TRACK_CHIP_TONES[t.tone || 'neutral']
                    }`}
                >
                    <span className="opacity-60">{TRACK_NAMES[key]}</span>
                    {t.done && t.tone !== 'bad' ? <Check size={11} /> : null}
                    {t.label}
                </span>
            ))}
        </div>
    );
};

const TRACK_TEXT_TONES = {
    urgent: 'font-bold text-danger-600 dark:text-danger-400',
    warn: 'font-semibold text-warning-text dark:text-warning-300',
    finance: 'font-semibold text-cyan-700 dark:text-cyan-300',
    info: 'text-sky-700 dark:text-sky-300',
    ok: 'text-emerald-600 dark:text-emerald-400',
    bad: 'text-danger-600 dark:text-danger-400',
    neutral: 'text-mono-500 dark:text-mono-400',
};

/**
 * Os 4 processos em COLUNAS FIXAS com micro-cabeçalho — mesma posição em toda
 * linha, para escanear em coluna. Não aplicável = "—" apagado; concluído fica
 * quieto (✓); pendente carrega cor e peso.
 */
export const TrackGrid = ({ rma }) => {
    const { ml, fisico, fabricante, financeiro } = ComputeTracks(rma);
    const cols = [
        ['Mercado Livre', ml],
        ['Galpão', fisico],
        ['Fabricante', fabricante],
        ['Financeiro', financeiro],
    ];
    return (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4">
            {cols.map(([name, t]) => (
                <div key={name} className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                        {name}
                    </p>
                    {t.na ? (
                        <p className="text-sm text-mono-300 dark:text-mono-700">—</p>
                    ) : (
                        <p
                            className={`truncate text-sm ${
                                t.done && t.tone !== 'bad'
                                    ? 'text-mono-400 dark:text-mono-500'
                                    : TRACK_TEXT_TONES[t.tone || 'neutral']
                            }`}
                            title={t.label}
                        >
                            {t.done ? '✓ ' : t.tone === 'urgent' ? '● ' : ''}
                            {t.label}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
};

// ── Stepper do pipeline ──────────────────────────────────────────────────────

/**
 * O caso tem fluxo FÍSICO (etiquetar → inspecionar → destinar)? Usa o
 * `physical_flow` do backend (RmaRepository.HasPhysicalFlow: venda não-Full,
 * ou devolução com destino `seller_address` — Full que volta ao NOSSO
 * endereço também, decisão do dono em 24/09); sem o campo, a mesma regra.
 */
export function HasPhysicalFlow(rma) {
    if (typeof rma?.physical_flow === 'boolean') {
        return rma.physical_flow;
    }
    return !rma?.is_full || rma?.return_destination === 'seller_address';
}

export const PipelineStepper = ({ rma }) => {
    const cancelled = rma.status === 'CANCELADO';
    // Venda Full cuja devolução vem ao NOSSO endereço tem etapa física (rodada
    // 7 — mesma regra do trilho físico em `ComputeTracks`).
    const fullNoPhysical = !HasPhysicalFlow(rma);
    const steps = fullNoPhysical ? ['ABERTO', 'RESOLVIDO'] : PIPELINE;
    const currentIndex = steps.indexOf(rma.status);
    return (
        <div className="flex items-center gap-0 overflow-x-auto">
            {steps.map((step, i) => {
                const isDone = !cancelled && currentIndex > i;
                const isCurrent = !cancelled && currentIndex === i;
                return (
                    <div key={step} className="flex shrink-0 items-center">
                        {i > 0 && (
                            <div
                                className={`h-0.5 w-8 ${isDone ? 'bg-emerald-400 dark:bg-emerald-500' : 'bg-mono-200 dark:bg-mono-800'}`}
                            />
                        )}
                        <div className="flex flex-col items-center gap-1 px-1">
                            <div
                                className={`flex h-6 w-6 items-center justify-center text-[10px] font-bold ${
                                    isDone
                                        ? 'bg-emerald-400 text-white dark:bg-emerald-500'
                                        : isCurrent
                                          ? 'bg-black text-white ring-4 ring-mono-200 dark:bg-white dark:text-black dark:ring-mono-800'
                                          : 'bg-mono-100 text-mono-400 dark:bg-mono-900 dark:text-mono-600'
                                }`}
                            >
                                {isDone ? <Check size={12} /> : i + 1}
                            </div>
                            <span
                                className={`whitespace-nowrap text-[10px] font-semibold ${
                                    isCurrent ? 'text-black dark:text-white' : 'text-mono-400 dark:text-mono-600'
                                }`}
                            >
                                {STATUS_META[step].label}
                            </span>
                        </div>
                    </div>
                );
            })}
            {cancelled && (
                <span className="ml-3">
                    <StatusPill status="CANCELADO" />
                </span>
            )}
            {fullNoPhysical && (
                <span className="ml-3">
                    <AlertTag tone="neutral">Full — sem etapa física</AlertTag>
                </span>
            )}
        </div>
    );
};

const Question = ({ label, value, onChange }) => (
    <div>
        <p className="mb-1.5 text-sm font-medium text-mono-700 dark:text-mono-300">{label}</p>
        <div className="flex gap-2">
            {[
                { v: false, text: 'Não' },
                { v: true, text: 'Sim' },
            ].map((opt) => (
                <button
                    key={String(opt.v)}
                    type="button"
                    onClick={() => onChange(opt.v)}
                    className={` border px-5 py-1.5 text-sm font-semibold transition-colors ${
                        value === opt.v
                            ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black'
                            : 'border-mono-300 text-mono-600 hover:border-mono-500 dark:border-mono-700 dark:text-mono-300'
                    }`}
                >
                    {opt.text}
                </button>
            ))}
        </div>
    </div>
);

// O backend aceita no máximo 10 arquivos por request de inspeção.
const INSPECTION_MAX_PHOTOS = 10;

// Inspeção guiada — fotos primeiro, depois as duas perguntas do fluxo.
export const InspectionModal = ({ isOpen, onClose, onConfirm, saving }) => {
    const [hasIssue, setHasIssue] = useState(null);
    const [hasDamage, setHasDamage] = useState(null);
    const [serialNumber, setSerialNumber] = useState('');
    const [note, setNote] = useState('');
    const [photos, setPhotos] = useState([]);
    const tooManyPhotos = photos.length > INSPECTION_MAX_PHOTOS;
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Inspeção do produto" size="sm">
            <div className="flex flex-col gap-4">
                <div>
                    <p className="mb-1.5 text-sm font-medium text-mono-700 dark:text-mono-300">
                        1. Fotos da mercadoria <span className="text-mono-400">(opcional por enquanto)</span>
                    </p>
                    <label className="flex cursor-pointer items-center justify-center border-2 border-dashed border-mono-300 px-4 py-5 text-sm text-mono-500 transition-colors hover:border-mono-500 dark:border-mono-700">
                        {photos.length
                            ? `${photos.length} foto(s) selecionada(s) — clique para trocar`
                            : 'Clique para anexar as fotos'}
                        <input
                            type="file"
                            multiple
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => setPhotos(Array.from(e.target.files || []))}
                        />
                    </label>
                    {tooManyPhotos && (
                        <p className="mt-1.5 text-xs font-medium text-danger-600 dark:text-danger-400">
                            Máximo de {INSPECTION_MAX_PHOTOS} fotos por inspeção — remova{' '}
                            {photos.length - INSPECTION_MAX_PHOTOS} para continuar.
                        </p>
                    )}
                </div>
                <Question
                    label="2. Algum problema na conferência? (não confere, faltando, caixa vazia…)"
                    value={hasIssue}
                    onChange={setHasIssue}
                />
                <Question label="3. Tem avaria?" value={hasDamage} onChange={setHasDamage} />
                <Input
                    label="4. Nº de série do produto"
                    value={serialNumber}
                    onChange={(e) => setSerialNumber(e.target.value)}
                    placeholder="ex.: SN-ABC123 (mais de um: separe por vírgula)"
                />
                {hasIssue === true && (
                    <p className=" bg-danger-50 p-2.5 text-xs text-danger-700 dark:bg-danger-400/10 dark:text-danger-300">
                        Problema na conferência é candidato a mediação no ML — o alerta ficará registrado na timeline.
                    </p>
                )}
                {hasDamage !== null && hasIssue !== null && (
                    <p className="text-xs text-mono-500">
                        Desfecho sugerido: <strong>{hasDamage ? 'Devolução ao fornecedor' : 'Reestoque'}</strong>
                    </p>
                )}
                <Textarea
                    label="Observações (opcional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                />
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        loading={saving}
                        disabled={hasIssue === null || hasDamage === null || tooManyPhotos}
                        onClick={() =>
                            onConfirm({ hasIssue, hasDamage, serialNumber: serialNumber.trim(), note, photos })
                        }
                    >
                        Confirmar inspeção
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

// ── Chat da mediação (balões) ────────────────────────────────────────────────

const CHAT_BUBBLE_STYLES = {
    respondent: {
        align: 'items-end',
        bubble: 'bg-black text-white dark:bg-white dark:text-black',
        label: 'Nós',
    },
    complainant: {
        align: 'items-start',
        bubble: 'bg-white text-mono-900 ring-1 ring-mono-200 dark:bg-mono-900 dark:text-mono-100 dark:ring-mono-800',
        label: 'Comprador',
    },
    mediator: {
        align: 'items-start',
        bubble: 'bg-warning-50 text-warning-900 ring-1 ring-warning-600/20 dark:bg-warning-400/10 dark:text-warning-200',
        label: 'Mediador ML',
    },
};

function cleanMessage(html) {
    return String(html || '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();
}

// `attachmentUrl(filename)` monta a URL autenticada do anexo (fotos de
// revisão/evidências que o ML anexa às mensagens) — vem da página, que sabe o
// id do RMA. Sem ela, cai no aviso "anexo no painel do ML".
export const MediationChat = ({ messages, loading, maxHeightClass = 'max-h-96', attachmentUrl = null }) => {
    const endRef = useRef(null);
    useEffect(() => {
        endRef.current?.scrollIntoView({ block: 'nearest' });
    }, [messages]);

    if (loading) {
        return (
            <div className="flex justify-center py-6">
                <LoadingSpinner />
            </div>
        );
    }
    if (!messages?.length) {
        return <p className="py-4 text-center text-xs text-mono-500">Sem mensagens nesta mediação.</p>;
    }
    return (
        <div className={`flex ${maxHeightClass} flex-col gap-3 overflow-y-auto bg-mono-50 p-4 dark:bg-mono-950`}>
            {messages.map((m, i) => {
                // Papel desconhecido NÃO cai no balão do comprador (atribuiria
                // a fala à pessoa errada) — balão neutro com o papel cru.
                const style = CHAT_BUBBLE_STYLES[m.sender_role] || {
                    align: 'items-start',
                    bubble: 'bg-mono-100 text-mono-700 ring-1 ring-mono-300 dark:bg-mono-900 dark:text-mono-300 dark:ring-mono-700',
                    label: m.sender_role || 'Sistema',
                };
                return (
                    <div key={i} className={`flex flex-col ${style.align}`}>
                        <span className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-mono-400 dark:text-mono-600">
                            {style.label} · {fmtDate(m.date_created)}
                            {m.stage === 'dispute' ? ' · disputa' : ''}
                        </span>
                        <div
                            className={`max-w-[80%] whitespace-pre-wrap px-4 py-2.5 text-sm leading-relaxed ${style.bubble}`}
                        >
                            {cleanMessage(m.message)}
                            {Array.isArray(m.attachments) && m.attachments.length > 0 && attachmentUrl && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {m.attachments.map((att, j) =>
                                        String(att.type || '').startsWith('image/') ? (
                                            <a
                                                key={j}
                                                href={attachmentUrl(att.filename)}
                                                target="_blank"
                                                rel="noreferrer"
                                                title={att.original_filename || att.filename}
                                            >
                                                <img
                                                    src={attachmentUrl(att.filename)}
                                                    alt={att.original_filename || 'Foto anexada pelo ML'}
                                                    loading="lazy"
                                                    className="h-32 w-32 object-cover ring-1 ring-black/10 transition-opacity hover:opacity-80"
                                                />
                                            </a>
                                        ) : (
                                            <a
                                                key={j}
                                                href={attachmentUrl(att.filename)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-[11px] font-semibold underline underline-offset-2 opacity-80 hover:opacity-100"
                                            >
                                                📎 {att.original_filename || att.filename}
                                            </a>
                                        ),
                                    )}
                                </div>
                            )}
                            {(m.attachments > 0 ||
                                (Array.isArray(m.attachments) && m.attachments.length > 0 && !attachmentUrl)) && (
                                <p className="mt-1.5 text-[10px] opacity-60">
                                    📎 {Array.isArray(m.attachments) ? m.attachments.length : m.attachments} anexo(s) no
                                    painel do ML
                                </p>
                            )}
                        </div>
                    </div>
                );
            })}
            <div ref={endRef} />
        </div>
    );
};

// ── Painel quieto (blocos de informação do detalhe) ──────────────────────────

export const Panel = ({ title, action = null, children, className = '' }) => (
    <section
        className={` border border-mono-200 bg-white shadow-sm-mono dark:border-mono-800 dark:bg-mono-950 ${className}`}
    >
        {title && (
            <header className="flex items-center justify-between gap-2 px-5 pb-0 pt-4">
                <h3 className="text-[11px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                    {title}
                </h3>
                {action}
            </header>
        )}
        <div className="flex flex-col gap-3 p-5 pt-3">{children}</div>
    </section>
);

export const Fact = ({ label, children, mono = false }) => (
    <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-mono-400 dark:text-mono-600">{label}</p>
        <p className={`truncate text-sm text-mono-900 dark:text-mono-100 ${mono ? 'font-mono' : ''}`}>
            {children || '—'}
        </p>
    </div>
);

// ── Checklist da inspeção (dono, 2026-09-23) ─────────────────────────────────
// Linhas ✓/⚠ do checklist (é o nosso? + transporte, embalagem do produto,
// componentes, produto) — tela final do assistente, caso (RmaDetail) e bloco
// "Definir destino" do supervisor. Só o CHECKLIST: nada de destino aqui.

const CHECK_MARK_CLASS = {
    ok: 'text-emerald-600 dark:text-emerald-400',
    problem: 'text-warning-text dark:text-warning-300',
    none: 'text-mono-400 dark:text-mono-600',
};

// Layout por tamanho. `lg` (tela do assistente — também no celular, a foto é
// pela câmera): abaixo de `sm` o rótulo vai ACIMA da resposta (com rótulo de
// largura fixa a resposta sumia em ~390 px). `xs` (coluna de 20rem do
// Histórico do pedido): rótulo e resposta em texto corrido, que quebra.
const CHECKLIST_LAYOUT = {
    sm: {
        list: 'text-[13px]',
        row: 'flex items-baseline gap-3 px-3 py-2',
        mark: 'w-5',
        pair: 'flex min-w-0 flex-1 items-baseline gap-3',
        label: 'w-40 shrink-0 font-semibold text-mono-600 dark:text-mono-300',
        sep: '',
    },
    lg: {
        list: 'text-base',
        row: 'flex items-start gap-3 px-3 py-3 sm:items-baseline sm:px-5',
        mark: 'w-5',
        pair: 'flex min-w-0 flex-1 flex-col sm:flex-row sm:items-baseline sm:gap-3',
        label: 'font-semibold text-mono-600 dark:text-mono-300 sm:w-56 sm:shrink-0',
        sep: '',
    },
    xs: {
        list: 'text-[12px] leading-snug',
        row: 'flex items-baseline gap-1.5 px-2 py-1',
        mark: 'w-4',
        pair: 'min-w-0 flex-1 break-words',
        label: 'text-mono-500 dark:text-mono-400',
        sep: ': ',
    },
};

/**
 * Props: `checklist` (`ChecklistFromRma` ou o resultado do assistente),
 * `serialNumber` (undefined = sem a linha da série; null/'' = "não tem
 * série"), `photoCounts` ({ transporte: n, … } — opcional, "n fotos" por
 * etapa), `size` 'sm' (caso) | 'lg' (tela final do assistente) | 'xs'
 * (Histórico do pedido).
 */
export const InspectionChecklist = ({ checklist, serialNumber = undefined, photoCounts = null, size = 'sm' }) => {
    const lines = ChecklistLines(checklist);
    if (!lines.length) {
        return null;
    }
    const layout = CHECKLIST_LAYOUT[size] || CHECKLIST_LAYOUT.sm;
    return (
        <ul
            className={`flex w-full flex-col divide-y divide-mono-100 border border-mono-200 bg-white dark:divide-mono-900 dark:border-mono-800 dark:bg-mono-950 ${layout.list}`}
        >
            {lines.map((line) => {
                const tone = line.ok === false ? 'problem' : line.ok ? 'ok' : 'none';
                return (
                    <li key={line.key} className={layout.row}>
                        <span className={`${layout.mark} shrink-0 text-center font-black ${CHECK_MARK_CLASS[tone]}`}>
                            {line.ok === false ? '⚠' : line.ok ? '✓' : '—'}
                        </span>
                        <span className={layout.pair}>
                            <span className={layout.label}>
                                {line.label}
                                {layout.sep}
                            </span>
                            <span
                                className={`min-w-0 break-words ${
                                    line.ok === false
                                        ? 'font-semibold text-black dark:text-white'
                                        : 'text-mono-700 dark:text-mono-200'
                                }`}
                            >
                                {line.value}
                            </span>
                        </span>
                        {photoCounts && photoCounts[line.key] !== undefined && (
                            <span className="shrink-0 text-[11px] text-mono-400 dark:text-mono-500">
                                {CountLabel(photoCounts[line.key], 'foto')}
                            </span>
                        )}
                    </li>
                );
            })}
            {serialNumber !== undefined && (
                <li className={layout.row}>
                    <span className={`${layout.mark} shrink-0 text-center font-black ${CHECK_MARK_CLASS.none}`}>#</span>
                    <span className={layout.pair}>
                        <span className={layout.label}>Nº de série{layout.sep}</span>
                        <span
                            className={`min-w-0 break-words ${
                                serialNumber ? 'font-mono text-black dark:text-white' : 'text-mono-500'
                            }`}
                        >
                            {serialNumber || 'não tem série'}
                        </span>
                    </span>
                </li>
            )}
        </ul>
    );
};

const PHOTO_GROUP_TITLES = {
    ...Object.fromEntries(INSPECTION_CHECK_STEPS.map((step) => [step.key, step.title])),
    [INSPECTION_PHOTO_UNSTAGED]: 'Inspeção (sem etapa)',
};

/**
 * Fotos da inspeção AGRUPADAS POR ETAPA (`GroupInspectionPhotos`): uma faixa
 * por etapa com as miniaturas (clique abre o original). Etapa sem foto não
 * aparece; `outros` (anexos avulsos) fica com quem chama.
 */
export const InspectionPhotoGroups = ({ groups, thumbClass = 'h-20 w-20' }) => {
    const keys = [...INSPECTION_CHECK_STEPS.map((step) => step.key), INSPECTION_PHOTO_UNSTAGED].filter(
        (key) => (groups?.[key] || []).length > 0,
    );
    if (!keys.length) {
        return null;
    }
    return (
        <div className="flex flex-col gap-3">
            {keys.map((key) => (
                <div key={key} className="flex flex-col gap-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-mono-400 dark:text-mono-600">
                        {PHOTO_GROUP_TITLES[key]} · {CountLabel(groups[key].length, 'foto')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {groups[key].map((attachment) =>
                            /\.(jpe?g|png|webp|gif)$/i.test(attachment.attachment_key || '') ? (
                                <a
                                    key={attachment.id}
                                    href={ResolveAssetUrl(attachment.attachment_key)}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <img
                                        src={ResolveAssetUrl(attachment.attachment_key)}
                                        alt={attachment.attachment_name || PHOTO_GROUP_TITLES[key]}
                                        loading="lazy"
                                        className={`${thumbClass} border border-mono-200 object-cover transition-transform hover:scale-105 dark:border-mono-800`}
                                    />
                                </a>
                            ) : (
                                <a
                                    key={attachment.id}
                                    href={ResolveAssetUrl(attachment.attachment_key)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="self-center bg-mono-100 px-2 py-1 text-xs underline-offset-2 hover:underline dark:bg-mono-900"
                                >
                                    📎 {attachment.attachment_name || 'anexo'}
                                </a>
                            ),
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

// ── Impressão de etiquetas físicas (100x60mm) ────────────────────────────────

export function PrintRmaLabels(labels, { rma = null, kind = 'receipt', toastError = null } = {}) {
    const html = BuildRmaLabelsHtml(labels, { rma, kind });
    if (!html) {
        toastError?.(
            kind === 'destination'
                ? 'A etiqueta de destino exige inspeção concluída, destino confirmado e etiqueta associada ao caso.'
                : 'Nenhuma etiqueta disponível para imprimir.',
        );
        return false;
    }
    // Sem 'noopener': com ele nas features o window.open retorna null por
    // especificação e nunca conseguiríamos escrever/imprimir. A página é
    // gerada localmente (about:blank + document.write) — sem risco de tabnabbing.
    const win = window.open('', '_blank', 'width=500,height=700');
    if (!win) {
        if (toastError) {
            toastError('Libere pop-ups para imprimir as etiquetas.');
        }
        return false;
    }
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
    return true;
}

// ── Modal de desfecho ────────────────────────────────────────────────────────

// `allowedResolutions` (destino da devolução, 2026-09-23): executar o destino
// definido pelo supervisor reusa este modal só com os desfechos daquele
// destino (`DESTINATION_RESOLUTIONS`); sem ele, todos, como sempre.
export const ResolveModal = ({
    isOpen,
    onClose,
    onConfirm,
    saving,
    defaultResolution = 'REESTOQUE',
    allowedResolutions = null,
    title = 'Resolver ocorrência',
    intro = null,
}) => {
    const options = Object.entries(RESOLUTION_LABELS).filter(
        ([value]) => !allowedResolutions || allowedResolutions.includes(value),
    );
    const [resolution, setResolution] = useState(
        options.some(([value]) => value === defaultResolution) ? defaultResolution : options[0]?.[0],
    );
    const [note, setNote] = useState('');
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
            <div className="flex flex-col gap-3">
                {intro && <p className="text-sm text-mono-600 dark:text-mono-300">{intro}</p>}
                <Select label="Desfecho" value={resolution} onChange={(e) => setResolution(e.target.value)}>
                    {options.map(([value, label]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </Select>
                {!intro && (
                    <p className="text-xs text-mono-500 dark:text-mono-400">
                        Reestoque, reparo e devolução ao fornecedor enfileiram a entrada no depósito certo do Bling — o
                        envio corre em segundo plano.
                    </p>
                )}
                <Textarea label="Nota (opcional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button loading={saving} onClick={() => onConfirm({ resolution, note })}>
                        Confirmar desfecho
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
